import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import normalizeCardPayload from '../../utils/normalizeCardPayload';
import { normalizeCardType } from '@cubemeet/core';
import { View, Text, StyleSheet, Modal, ScrollView, TextInput, Platform, TouchableOpacity, ActivityIndicator, Linking } from 'react-native';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import L, { type LatLngExpression } from 'leaflet';
import SafeView from '../common/SafeView';
import FullscreenPoiModalHeader from './FullscreenPoiModalHeader';
import CenteredContentWrapper from './CenteredContentWrapper';
import FullscreenPoiModalActions from './FullscreenPoiModalActions';
import PoiTitleAddressSection from './PoiTitleAddressSection';
import CardTypeSelector from './CardTypeSelector';
import { CreatorInfo as ShareCreatorInfo } from '../common/ShareButton';
import ShareSection from './ShareSection';
import PlatformIcon from '../common/PlatformIcon';
import MetaRow from './MetaRow';
import BookingPlatformsRow from '../cards/shared/BookingPlatformsRow'; // kept for legacy reference (BookingTab uses it)
import DetailsTab from './tabs/DetailsTab';
import DescriptionTab from './tabs/DescriptionTab';
import BookingTab from './tabs/BookingTab';
import NearbyTab from './tabs/NearbyTab';
import { isWebsiteLabel } from './placeholder-config';
import { ORANGE_ACTION } from '../../constants/colors';
// Social & map/address helpers now imported from atomic utils
import { pickSocialIcon } from './utils/socialUtils';
import { isAddressLabel, isCoordinateLabel } from './utils/metaUtils';
import { extractAddressFromMeta, firstAddressFrom } from './utils/addressUtils';
import { buildMapUrl } from './utils/mapUtils';
import { extractCreatorId, isValidId, pickInlineCreator } from './utils/creatorUtils';
import { usePoiEditing } from './hooks/usePoiEditing';
import { useUrlParser } from '../../hooks/useUrlParser';

type ModalCreatorInfo = ShareCreatorInfo & { cardId?: string | null };

// Helper component to parse and render attribution HTML
const AttributionLine: React.FC<{ html: string }> = ({ html }) => {
  try {
    const anchorMatch = String(html || '').match(/<a\s+href=['"]([^'"]+)['"][^>]*>(.*?)<\/a>/i);
    if (anchorMatch) {
      const url = anchorMatch[1];
      const text = anchorMatch[2];
      return (
        <Text 
          style={{ color: '#fff', fontSize: 11 }} 
          onPress={() => {
            try {
              if (typeof window !== 'undefined' && (window as any).open) {
                (window as any).open(url, '_blank');
              } else if (Linking && Linking.openURL) {
                Linking.openURL(url);
              }
            } catch (e) {
              console.error('Failed to open URL:', e);
            }
          }}
        >
          圖片來源：
          <Text style={{ textDecorationLine: 'underline' }}>{text}</Text>
        </Text>
      );
    }
  } catch (e) {
    console.error('Failed to parse attribution:', e);
  }
  return <Text style={{ color: '#fff', fontSize: 11 }}>{String(html || '')}</Text>;
};

export interface FullscreenPoiProps {
  visible: boolean;
  onClose: () => void;
  title: string;
  imageUrl?: string;
  address?: string;
  description?: string;
  metaData?: Array<{ label: string; value: string }>;
  tags?: string[];
  rawData?: Record<string, any>;
  // optional: full card object (id, owner_id, group_id, visibility, last_edited_by, last_edited_at)
  card?: any;
  // whether current user can edit (caller should compute based on group/owner)
  canEdit?: boolean;
  isOwner?: boolean;
  // callback: receives updated fields (must include id) and should return a Promise that resolves with updated card
  onSave?: (updated: any) => Promise<any>;
  onDelete?: () => Promise<any>;
  // when true the modal should open directly in edit mode (useful for create flows)
  initialEditMode?: boolean;
  // when true content should prefer inline editing UX (hide form actions)
  editableInline?: boolean;
  // optional supabase client for modal to perform DB ops directly
  supabaseClient?: any;
  currentUserId?: string;
  // callback when modal fetches canonical card row (useful to sync editor state)
  onLoadedCanonical?: (card: any) => void;
  // callback when creator avatar is clicked (for navigation)
  onCreatorPress?: (creatorId: string) => void;
  // optional handler used by hosts to present their own map selection modal
  onRequestMapNavigation?: (payload: {
    address?: string;
    coords?: { lat: number; lng: number } | null;
    title?: string;
    location?: any;
    card?: any;
  }) => void;
}

const FullscreenPoiModal: React.FC<FullscreenPoiProps> = ({ visible, onClose, title, imageUrl, address, description, metaData = [], tags = [], rawData, card, canEdit = false, isOwner = false, onSave, onDelete, supabaseClient, currentUserId, onLoadedCanonical, onCreatorPress, initialEditMode = false, editableInline = false, onRequestMapNavigation }) => {
  console.log('[FullscreenPoiModal] Component mounted/updated:', {
    visible,
    hasOnSave: typeof onSave === 'function',
    canEdit,
    isOwner,
    hasCard: !!card,
    cardKeys: card ? Object.keys(card) : [],
    rawDataKeys: rawData ? Object.keys(rawData) : [],
    rawDataMetaPairs: rawData && Array.isArray(rawData.metaPairs) ? rawData.metaPairs : undefined,
  });
  

  // Normalize different card/payload shapes we see across the app.
  // Some callers pass { card: { ... } }, others pass a wrapper like { visible: true, payload: { ... } },
  // and some pass rawMergedPayload inside rawData. This helper returns the most likely "card-like"
  // object that contains title/description/image fields.
  const normalizeCardForModal = (c: any) => {
    if (!c) return null;
    let candidate = c;
    try {
      // unwrap .card wrapper only if it looks like a real card object
      if (candidate.card && typeof candidate.card === 'object') {
        const cc = candidate.card;
        const looksReal = !!(cc && (cc.id || cc.cardId || cc.card_id || cc.title || cc.base_payload || cc.payload || cc.card_type));
        if (looksReal) candidate = cc; // otherwise keep the outer payload (common for new-card temp payloads with card:{})
      }
      // if wrapper has .payload which contains card-like fields, prefer payload
      if (candidate.payload && typeof candidate.payload === 'object') {
        const p = candidate.payload;
        // prefer payload when it contains any identifying card fields
        if (p.title || p.description || p.image || p.image_url || p.cover_image || p.cardId || p.card_id || p.id) return p;
      }
      // sometimes the canonical card is embedded under rawMergedPayload
      if (candidate.rawMergedPayload && typeof candidate.rawMergedPayload === 'object') return candidate.rawMergedPayload;
      return candidate;
    } catch (e) {
      return candidate;
    }
  };

  const normalizedCard = normalizeCardForModal(card);
  const cardForRender = normalizedCard || (card && (card.card || card)) || {};

  const [createdCardId, setCreatedCardId] = useState<string | null>(null);
  // Track whether viewport is wide (desktop) so we can remove inner content padding only on wide screens
  const [isWide, setIsWide] = useState<boolean>(() => {
    try { return (typeof window !== 'undefined' && window.innerWidth >= 900); } catch (e) { return true; }
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = () => setIsWide(window.innerWidth >= 900);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  // Prevent background (document/body) scrolling on web while modal is open to avoid double scrollbars
  const prevOverflowRef = useRef<{ body?: string; doc?: string } | null>(null);
  // Track if localMeta has been initialized to prevent overwriting user edits
  const localMetaInitializedRef = useRef(false);
  // Embed input modal state
  const [showEmbedModal, setShowEmbedModal] = useState(false);
  const [embedInput, setEmbedInput] = useState('');
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const b: any = typeof document !== 'undefined' ? document.body : null;
    const d: any = typeof document !== 'undefined' ? document.documentElement : null;
    if (!b || !d) return;
    if (visible) {
      prevOverflowRef.current = { body: b.style.overflow, doc: d.style.overflow };
      b.style.overflow = 'hidden';
      d.style.overflow = 'hidden';
      // Reset initialization flag when modal opens
      localMetaInitializedRef.current = false;
    } else if (prevOverflowRef.current) {
      b.style.overflow = prevOverflowRef.current.body || '';
      d.style.overflow = prevOverflowRef.current.doc || '';
      prevOverflowRef.current = null;
    }
    return () => {
      if (prevOverflowRef.current) {
        try { b.style.overflow = prevOverflowRef.current.body || ''; } catch {}
        try { d.style.overflow = prevOverflowRef.current.doc || ''; } catch {}
        prevOverflowRef.current = null;
      }
    };
  }, [visible]);

  const activeCardId = useMemo(() => {
    const candidates = [
      createdCardId,
      normalizedCard?.id,
      normalizedCard?.cardId,
      normalizedCard?.card_id,
      card?.id,
      card?.cardId,
      card?.card_id,
      card?.card?.id,
      card?.card?.cardId,
      rawData?.rawMergedPayload?.id,
      rawData?.rawMergedPayload?.cardId,
      rawData?.id,
      rawData?.cardId,
    ];
    for (const candidate of candidates) {
      if (candidate === null || typeof candidate === 'undefined') continue;
      const str = String(candidate).trim();
      if (str) return str;
    }
    return null;
  }, [normalizedCard, card, rawData, createdCardId]);

  const pickInlineCreatorWrapped = (src: any): ModalCreatorInfo | null => {
    const r = pickInlineCreator(src, activeCardId);
    return r ? { ...r } : null;
  };

  // Helper function to robustly extract card ID from various payload structures
  const extractCardId = (): string | null => {
    try {
      // Prefer newly created id if exists
      if (createdCardId) return createdCardId;
      // Try normalizedCard paths first
      if (normalizedCard && normalizedCard.id) return normalizedCard.id;
      if (normalizedCard && normalizedCard.cardId) return normalizedCard.cardId;
      if (normalizedCard && normalizedCard.card_id) return normalizedCard.card_id;
      
      // Try card object paths (check payload before top-level id)
      if (card && card.payload && card.payload.cardId) return card.payload.cardId;
      if (card && card.payload && card.payload.card_id) return card.payload.card_id;
      if (card && card.payload && card.payload.id) return card.payload.id;
      if (card && card.id) return card.id;
      if (card && card.cardId) return card.cardId;
      if (card && card.card_id) return card.card_id;
      if (card && card.card && card.card.id) return card.card.id;
      if (card && card.card && card.card.cardId) return card.card.cardId;
      
      // Try rawData paths
      if (rawData && rawData.rawMergedPayload && rawData.rawMergedPayload.id) return rawData.rawMergedPayload.id;
      if (rawData && rawData.rawMergedPayload && rawData.rawMergedPayload.cardId) return rawData.rawMergedPayload.cardId;
      if (rawData && rawData.id) return rawData.id;
      if (rawData && rawData.cardId) return rawData.cardId;
      
      // No cardId found — this is NORMAL for itinerary items (not cards_cards entries)
      // Only log if we have indicators that this SHOULD be a card (e.g., card.card_type)
      if (visible && (card?.card_type || normalizedCard?.card_type || rawData?.card_type)) {
        console.debug('[FullscreenPoiModal] Unable to extract cardId from card-type object:', { card, normalizedCard, rawData });
      }
      return null;
    } catch (e) {
      console.error('[FullscreenPoiModal] Error extracting cardId:', e);
      return null;
    }
  };

  // derive ownership/permission locally when caller did not provide explicit flags
  const [fetchedCreatorId, setFetchedCreatorId] = useState<string | null>(null);
  const [creatorInfo, setCreatorInfo] = useState<ModalCreatorInfo | null>(null);
  const [dataReady, setDataReady] = useState<boolean>(false);
  // Canonical DB row (cards_cards) — used as ground truth for non-editable fields like coordinates
  const [canonicalCard, setCanonicalCard] = useState<any | null>(null);

  useEffect(() => {
    if (!visible) {
      setCreatorInfo(null);
      setFetchedCreatorId(null);
      setDataReady(false);
      return;
    }
    // when modal becomes visible, start in not-ready state until we finish loading canonical card/creator
    setDataReady(false);
  }, [visible]);

  // normalize and validate creator id values: prefer resolved creator info, otherwise fall back to extracted ids
  const extractedCreator = extractCreatorId(card);
  const derivedCreatorId = isValidId(creatorInfo?.id)
    ? String(creatorInfo?.id)
    : isValidId(fetchedCreatorId)
      ? String(fetchedCreatorId)
      : (isValidId(extractedCreator) ? String(extractedCreator) : null);
  // honor explicit boolean props only when they are true booleans; otherwise compute from derivedCreatorId
  const explicitIsOwner = (typeof isOwner === 'boolean') ? isOwner : false;
  const isNewCard = !activeCardId; // New/unsaved cards are always editable by current user
  const derivedIsOwner = isNewCard || explicitIsOwner || (isValidId(derivedCreatorId) && isValidId(currentUserId) && String(derivedCreatorId) === String(currentUserId));
  const explicitCanEdit = (typeof canEdit === 'boolean') ? canEdit : false;
  const derivedCanEdit = explicitCanEdit || derivedIsOwner;
  // build a set of lowercased meta labels so we can avoid rendering duplicates
  const metaLabelsLower = new Set((Array.isArray(metaData) ? metaData : []).map((m) => (m.label || '').trim().toLowerCase()));
  const hasMetaLabel = (...labels: string[]) => labels.some((l) => metaLabelsLower.has(l.trim().toLowerCase()));
  const shareCreator = useMemo<ShareCreatorInfo | undefined>(() => {
    if (!creatorInfo || !creatorInfo.id) return undefined;
    return {
      id: String(creatorInfo.id),
      display_name: creatorInfo.display_name || 'Unknown User',
      avatar_url: creatorInfo.avatar_url || undefined,
      bio: creatorInfo.bio || undefined,
    };
  }, [creatorInfo]);
  // NOTE: derivedCardType moved below state declarations to avoid TDZ when reading localBasePayload

  // NOTE: normalizeUploadType and uploadCardType moved below state declarations to avoid TDZ
  // Editing state and handlers are now encapsulated by usePoiEditing
  const {
    editMode,
    setEditMode,
    saving,
    localTitle,
    setLocalTitle,
    localDescription,
    setLocalDescription,
    localImage,
    setLocalImage,
    localAddress,
    setLocalAddress,
    localMeta,
    setLocalMeta,
    localTags,
    setLocalTags,
    localBasePayload,
    setLocalBasePayload,
    basePayloadRef,
    pendingCoverFile,
    setPendingCoverFile,
    patchBasePayload: handleBasePayloadPatch,
    startEdit,
    save,
    deleteCard,
    onCoverUrl,
    onCoverFileSelected,
    derivedCardType: hookDerivedCardType,
    uploadCardType: hookUploadCardType,
  } = usePoiEditing({
    initialEditMode,
    normalizedCard,
    card,
    rawData,
    title,
    description,
    address,
    imageUrl,
    tags,
    supabaseClient,
    currentUserId,
    onSaveExternal: onSave,
    onDeleteExternal: onDelete,
    onLoadedCanonical,
    createdCardId,
    setCreatedCardId: (id) => setCreatedCardId(id),
  });

  // URL Parser for external links
  const { loading: parsingUrl, meta: parsedMeta, extractMeta, setMeta: setParsedMeta } = useUrlParser();

  // Handle embed input for video cards
  const handleEmbedInput = useCallback((input: string) => {
    console.log('[FullscreenPoiModal] handleEmbedInput:', input);
    
    if (!input || !input.trim()) {
      setShowEmbedModal(false);
      setEmbedInput('');
      return;
    }
    
    const trimmed = input.trim();
    
    // 1. 尝试从 iframe 代码中提取 src
    const iframeMatch = trimmed.match(/<iframe[^>]+src=["']([^"']+)["']/i);
    if (iframeMatch && iframeMatch[1]) {
      const src = iframeMatch[1];
      console.log('[FullscreenPoiModal] Extracted src from iframe:', src);
      
      // 检测平台并构建完整的 embed 对象（匹配行程表格式）
      let provider = 'unknown';
      let videoId = null;
      let poster = null;
      let siteName = null;
      let favicon = null;
      
      if (src.includes('youtube.com') || src.includes('youtu.be')) {
        provider = 'youtube';
        const ytMatch = src.match(/(?:youtube\.com\/embed\/|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
        if (ytMatch) {
          videoId = ytMatch[1];
          poster = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
          siteName = 'YouTube';
          favicon = 'https://www.google.com/s2/favicons?domain=youtube.com&sz=64';
        }
      } else if (src.includes('bilibili.com')) {
        provider = 'bilibili';
        const bvMatch = src.match(/\/video\/(BV[a-zA-Z0-9]+)/);
        if (bvMatch) {
          videoId = bvMatch[1];
          siteName = 'Bilibili';
          favicon = 'https://www.google.com/s2/favicons?domain=bilibili.com&sz=64';
        }
      }
      
      // 构建与行程表一致的格式 - 只更新需要的字段
      const embedData = {
        id: videoId,
        src: src.includes('?') ? src : `${src}?playsinline=1&enablejsapi=1`,
        poster,
        provider,
      };
      
      // 使用 patch 方式更新，保留其他字段
      handleBasePayloadPatch({
        embed: embedData,
        type: 'external',
        source: {
          at: new Date().toISOString(),
          kind: 'manual-embed',
        },
        favicon,
        siteName,
        website: null,
        imageUrl: poster,
      });
      
      console.log('[FullscreenPoiModal] Saved iframe embed:', embedData);
      
      setShowEmbedModal(false);
      setEmbedInput('');
      return;
    }
    
    // 2. 如果不是 iframe，尝试作为 URL 解析（YouTube）
    const parseYouTubeUrl = (url: string): string | null => {
      try {
        const patterns = [
          /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
          /^([a-zA-Z0-9_-]{11})$/ // 直接输入视频 ID
        ];
        
        for (const pattern of patterns) {
          const match = url.match(pattern);
          if (match && match[1]) {
            return match[1];
          }
        }
        return null;
      } catch (e) {
        return null;
      }
    };
    
    const videoId = parseYouTubeUrl(trimmed);
    if (videoId) {
      const src = `https://www.youtube.com/embed/${videoId}?playsinline=1&enablejsapi=1`;
      const poster = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
      
      const embedData = {
        id: videoId,
        src,
        poster,
        provider: 'youtube',
      };
      
      // 使用 patch 方式更新，保留其他字段
      handleBasePayloadPatch({
        embed: embedData,
        type: 'external',
        source: {
          at: new Date().toISOString(),
          kind: 'manual-embed',
        },
        favicon: 'https://www.google.com/s2/favicons?domain=youtube.com&sz=64',
        siteName: 'YouTube',
        website: null,
        imageUrl: poster,
      });
      
      console.log('[FullscreenPoiModal] Parsed YouTube URL:', embedData);
      
      setShowEmbedModal(false);
      setEmbedInput('');
      return;
    }
    
    // 3. 无法识别
    console.warn('[FullscreenPoiModal] Failed to parse embed input:', trimmed);
    if (typeof alert !== 'undefined') {
      alert('無法識別嵌入代碼，請確保是有效的 iframe 代碼或 YouTube URL');
    }
  }, [handleBasePayloadPatch, localTitle]);

  // Wrapper for setLocalMeta that always marks as initialized when called by user
  const setLocalMetaSafe = useCallback((nextOrUpdater: any) => {
    console.log('[FullscreenPoiModal] setLocalMetaSafe called, marking as initialized');
    setLocalMeta(nextOrUpdater);
    // Mark as initialized to prevent future resets (both in modal and hook)
    localMetaInitializedRef.current = true;
    // Also mark in usePoiEditing hook to prevent it from resetting
    if (typeof window !== 'undefined') {
      (window as any).__localMetaUserEdited = true;
    }
  }, []);

  useEffect(() => {
    console.log('[FullscreenPoiModal DEBUG] editMode state (visible change)', {
      visible,
      initialEditMode,
      editMode,
    });
  }, [visible]);

  useEffect(() => {
    console.log('[FullscreenPoiModal DEBUG] editMode state (editMode change)', {
      visible,
      initialEditMode,
      editMode,
    });
  }, [editMode, visible]);

  // Hard guarantee: if这是占位卡片或父层要求 initialEditMode，始终保持编辑模式（除非用户主动取消/保存切换）。
  useEffect(() => {
    if (!visible) return;
    const isPlaceholderCard = Boolean((card as any)?.meta?.isPlaceholder || (card as any)?._item?.meta?.isPlaceholder);
    if ((initialEditMode || isPlaceholderCard) && !editMode) {
      // 立即切到编辑模式，避免因其它异步 effect 覆盖导致先显示浏览后再跳编辑的闪烁
      setEditMode(true);
    }
  }, [visible, initialEditMode, card, editMode]);
  // Helpers: map/link rendering for special meta rows (坐标 / 网站) now imported
  // Website label matcher and placeholders come from centralized config
  const displayedMeta = useMemo(() => {
    const result = Array.isArray(localMeta) ? localMeta.filter((p) => !isAddressLabel(p?.label) && !isCoordinateLabel(p?.label)) : [];
    console.log('[FullscreenPoiModal] displayedMeta computed:', {
      localMetaLength: localMeta?.length || 0,
      displayedMetaLength: result.length,
      localMeta,
      displayedMeta: result,
    });
    return result;
  }, [localMeta]);
  // Tab state: details | description (extensible for future: reviews, notes, etc.)
  const [activeTab, setActiveTab] = useState<'details'|'description'|'booking'|'nearby'>('details');
  // Hidden file input ref for cover image upload
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // Extract coordinates for nearby spots (simple heuristic similar to CardDetailPage)
  const extractCoords = useCallback((): { lat: number; lng: number } | null => {
    const read = (value: any): { lat: number; lng: number } | null => {
      if (!value) return null;
      if (typeof value === 'object') {
        const lat = typeof (value as any).lat === 'number' ? (value as any).lat : (typeof (value as any).latitude === 'number' ? (value as any).latitude : null);
        const lng = typeof (value as any).lng === 'number' ? (value as any).lng : (typeof (value as any).longitude === 'number' ? (value as any).longitude : null);
        if (lat !== null && lng !== null) return { lat, lng };
        if (Array.isArray((value as any).coordinates) && (value as any).coordinates.length >= 2) {
          const a = (value as any).coordinates.map((n: any) => Number(n));
          if (a.every((n: number) => !Number.isNaN(n))) {
            // assume [lng, lat] if first out of lat range
            if (Math.abs(a[0]) <= 90) return { lat: a[0], lng: a[1] };
            return { lat: a[1], lng: a[0] };
          }
        }
      }
      if (typeof value === 'string' && value.includes(',')) {
        const parts = value.split(',').map(p => Number(p.trim()));
        if (parts.length >= 2 && parts.every(p => !Number.isNaN(p))) {
          if (Math.abs(parts[0]) <= 90) return { lat: parts[0], lng: parts[1] };
          return { lat: parts[1], lng: parts[0] };
        }
      }
      return null;
    };
    
    // CRITICAL: Also check localMeta (metaPairs) for "坐标" field
    // This is where coordinates are stored in itinerary items
    if (Array.isArray(localMeta)) {
      const coordPair = localMeta.find((p: any) => 
        p && (p.label === '坐标' || p.label === '坐標' || p.label === 'coordinates' || p.label === 'Coordinates')
      );
      if (coordPair && coordPair.value) {
        const r = read(coordPair.value);
        if (r && r.lat !== 0 && r.lng !== 0) return r;
      }
    }
    
    const sources = [
      localBasePayload,
      (localBasePayload as any)?.location,
      normalizedCard,
      normalizedCard?.base_payload,
      normalizedCard?.payload,
      (normalizedCard as any)?.location,
      card,
      card?.payload,
      (card as any)?.location,
      rawData,
      rawData?.rawMergedPayload,
      rawData?.rawMergedPayload?.location,
      address,
    ];
    for (const s of sources) {
      const r = read(s);
      if (r) return r;
    }
    return null;
  }, [localBasePayload, normalizedCard, card, rawData, address, localMeta]);
  
  // Use useMemo so nearbyCoords updates when localMeta changes
  const nearbyCoords = useMemo(() => extractCoords(), [extractCoords]);
  
  // DEBUG: Log coordinates extraction result
  useEffect(() => {
    if (visible) {
      console.log('[FullscreenPoiModal] nearbyCoords extracted:', nearbyCoords);
      // Also log the coordinate pair from localMeta if present
      const coordPair = Array.isArray(localMeta) ? localMeta.find((p: any) => 
        p && (p.label === '坐标' || p.label === '坐標' || p.label === 'coordinates')
      ) : null;
      console.log('[FullscreenPoiModal] data sources:', {
        localBasePayload,
        normalizedCard,
        card,
        rawData,
        address,
        localMeta_coordPair: coordPair,
        localMeta_length: localMeta?.length,
      });
    }
  }, [visible, nearbyCoords, localMeta]);
  
  // pendingCoverFile/basePayloadRef managed by hook
  // Resolve card kind early for placeholder selection to avoid TDZ with later hooks
  const resolveKindForPlaceholders = useCallback((): string => {
    const candidates = [
      normalizedCard?.card_type,
      normalizedCard?.cardType,
      card?.card_type,
      card?.cardType,
      (card && (card as any).type) || undefined,
      rawData?.rawMergedPayload?.card_type,
      rawData?.card_type,
    ];
    for (const c of candidates) {
      if (typeof c === 'string' && c.trim()) return String(c);
    }
    return 'poi';
  }, [normalizedCard, card, rawData]);
  // Note: inline edits are local-only; persistence happens when pressing the main Save action
  // Reset local state when switching to a different card to avoid leaking previous card's payload/fields
  const lastCardIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!visible) return;
    const curr = activeCardId || null;
    const prev = lastCardIdRef.current;
    if (curr && prev && curr !== prev) {
      // Clear local caches; the init effect below will rehydrate from the new card
      setLocalBasePayload({});
      setLocalTitle(title || '');
      setLocalDescription(description || '');
      setLocalAddress(address || undefined);
      setLocalTags([]);
      // IMPORTANT: when switching cards, immediately hydrate metaPairs from the authoritative
      // itinerary snapshot to avoid a brief empty "詳細信息" state caused by a later reset effect.
      try {
        // CRITICAL: Also consider metaData prop passed from parent (e.g., PoiModalWrapper)
        const freshMeta = (Array.isArray(metaData) && metaData.length > 0)
          ? metaData
          : (card && card.custom_payload && Array.isArray(card.custom_payload.metaPairs))
            ? card.custom_payload.metaPairs
            : [];
        setLocalMeta(freshMeta); // Use as-is, no forced placeholders
        localMetaInitializedRef.current = freshMeta.length > 0; // Mark initialized if we have data
      } catch (e) { setLocalMeta([]); localMetaInitializedRef.current = false; }
    }
    lastCardIdRef.current = curr;
  }, [activeCardId, visible]);
  const prevVisible = useRef<boolean>(false);
  
  // Reset editMode when modal closes to ensure clean state on next open
  useEffect(() => {
    if (!visible && prevVisible.current) {
      // Modal just closed, reset edit mode
      setEditMode(false);
    }
    prevVisible.current = visible;
  }, [visible]);
  
  // CRITICAL: Load fresh data from metaData prop or card.custom_payload when modal opens
  useEffect(() => {
    if (visible && !localMetaInitializedRef.current) {
      // Prefer metaData prop from parent (already has authoritative data)
      const freshMeta = (Array.isArray(metaData) && metaData.length > 0)
        ? metaData
        : card?.custom_payload?.metaPairs;
      
      if (Array.isArray(freshMeta) && freshMeta.length > 0) {
        console.log('[FullscreenPoiModal] Loading metaPairs:', { count: freshMeta.length, fromProp: Array.isArray(metaData) && metaData.length > 0 });
        setLocalMeta(freshMeta); // Use as-is, no forced placeholders
        localMetaInitializedRef.current = true;
      }
    }
  }, [visible, card, metaData]);
  
  const [bottomInset, setBottomInset] = useState<number>(250);

  const clonePayload = (value: any) => {
    if (!value || typeof value !== 'object') return value ?? null;
    try {
      return JSON.parse(JSON.stringify(value));
    } catch (e) {
      if (Array.isArray(value)) return value.slice();
      return { ...value };
    }
  };

  // Type hint based solely on incoming props (no local state), for new cards with no id
  const incomingTypeHint = useMemo(() => {
    const candidates = [
      normalizedCard?.card_type,
      normalizedCard?.cardType,
      card?.card_type,
      card?.cardType,
      (card && card.type) || undefined,
      rawData?.rawMergedPayload?.card_type,
      rawData?.card_type,
    ];
    for (const c of candidates) {
      if (typeof c === 'string' && c.trim()) return c;
    }
    return undefined;
  }, [normalizedCard, card, rawData]);

  // Reset local caches when opening a different card OR a different new-card type
  const lastOpenKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!visible) return;
    const key = activeCardId ? `id:${activeCardId}` : `new:${(incomingTypeHint || 'unknown')}`;
    if (lastOpenKeyRef.current !== key) {
      // clear caches so the subsequent init effect hydrates from fresh payload
      setLocalBasePayload({});
      setLocalTitle(title || '');
      setLocalDescription(description || '');
      setLocalAddress(address || undefined);
      setLocalTags([]);
      // Hydrate metaPairs right away if itinerary snapshot is present.
      // IMPORTANT: for placeholder create flow (initialEditMode=true), do NOT wipe existing
      // localMeta to [] here — the editing hook may have just generated default placeholders.
      try {
        // CRITICAL: Also consider metaData prop passed from parent (e.g., PoiModalWrapper)
        const freshMeta = (Array.isArray(metaData) && metaData.length > 0)
          ? metaData
          : (card && card.custom_payload && Array.isArray(card.custom_payload.metaPairs))
            ? card.custom_payload.metaPairs
            : [];
        if (Array.isArray(freshMeta) && freshMeta.length > 0) {
          console.log('[FullscreenPoiModal] lastOpenKey: applying metaPairs', { count: freshMeta.length, fromProp: Array.isArray(metaData) && metaData.length > 0 });
          setLocalMeta(freshMeta); // Use as-is
          localMetaInitializedRef.current = true; // Mark as initialized since we have good data
        } else {
          // When creating new placeholders, keep whatever meta the hook injected
          // IMPORTANT: If rawData has metaPairs (e.g., from OSM), don't clear them even in view mode
          const hasRawDataMeta = rawData && Array.isArray(rawData.metaPairs) && rawData.metaPairs.length > 0;
          if (!initialEditMode && !hasRawDataMeta) {
            console.log('[FullscreenPoiModal] lastOpenKey: no snapshot meta; clearing localMeta (view mode)');
            setLocalMeta([]);
            localMetaInitializedRef.current = false;
          } else {
            console.log('[FullscreenPoiModal] lastOpenKey: no snapshot meta; preserve existing localMeta', {
              initialEditMode,
              hasRawDataMeta,
            });
          }
        }
      } catch (e) {
        if (!initialEditMode) { setLocalMeta([]); localMetaInitializedRef.current = false; }
      }
      lastOpenKeyRef.current = key;
    }
  }, [visible, activeCardId, title, description, address]);

  // After base payload state exists, derive the most likely card_type for UI and upload.
  const derivedCardType = useMemo(() => {
    if (hookDerivedCardType) return hookDerivedCardType;
    const candidates = [
      normalizedCard?.card_type,
      normalizedCard?.cardType,
      card?.card_type,
      card?.cardType,
      (card && card.type) || undefined,
      // consider any hints embedded in the local/base payload
      (localBasePayload && (localBasePayload.card_type || (localBasePayload as any).type)) || undefined,
      rawData?.rawMergedPayload?.card_type,
      rawData?.card_type,
    ];
    for (const candidate of candidates) {
      if (typeof candidate === 'string' && candidate.trim()) return candidate;
    }
    return undefined;
  }, [normalizedCard, card, rawData, localBasePayload]);

  // Auto-fill form when meta is extracted
  useEffect(() => {
    if (parsedMeta && !parsedMeta.error) {
      // If we are in "external" mode (link preview), we do NOT auto-save.
      // We just let the user see the preview in the modal.
      const isExternalMode = derivedCardType === 'external' || incomingTypeHint === 'external';
      
      if (!isExternalMode) {
        // For video/other modes, we might still want auto-save behavior or 
        // maybe we should unify this? For now, let's keep the old behavior for video
        // which was "auto-fill and maybe save if it was a direct paste"?
        // Actually, the previous logic was just auto-filling local state.
        
        if (parsedMeta.title) setLocalTitle(parsedMeta.title);
        if (parsedMeta.description) setLocalDescription(parsedMeta.description);
        if (parsedMeta.image) setLocalImage(parsedMeta.image);
        
        // If it's a video embed, we might want to auto-patch the payload
        if (parsedMeta.embed) {
           handleBasePayloadPatch({
             embed: parsedMeta.embed,
             card_type: 'video'
           });
           // Close modal if it was the video embed modal
           if (showEmbedModal) setShowEmbedModal(false);
        }
      }
    }
  }, [parsedMeta, derivedCardType, incomingTypeHint, showEmbedModal]);

  // After localBasePayload/basePayloadRef exist, compute a stable upload cardType to avoid 'temp' path
  const uploadCardType = useMemo<'lodging' | 'restaurant' | 'poi'>(() => {
    if (hookUploadCardType) return hookUploadCardType;
    const mapped = normalizeCardType(derivedCardType, { fallback: null });
    if (mapped) return mapped;
    const p: any = (basePayloadRef.current || localBasePayload || {});
    const lodgingSignals = !!(p.starRating || p.star || p.hotelName || p.checkInTime || p.checkOutTime || (Array.isArray(p.amenities) && p.amenities.length));
    const restaurantSignals = !!(
      (Array.isArray(p.cuisine) && p.cuisine.length) || p.cuisine ||
      (Array.isArray(p.signatureDishes) && p.signatureDishes.length) || p.signatureDishes ||
      p.duration_minutes
    );
    if (lodgingSignals) return 'lodging';
    if (restaurantSignals) return 'restaurant';
    return 'poi';
  }, [derivedCardType, localBasePayload]);

  const payloadsEqual = (a: any, b: any) => {
    if (a === b) return true;
    if (!a || !b) return false;
    try {
      return JSON.stringify(a) === JSON.stringify(b);
    } catch (e) {
      return false;
    }
  };

  // handleBasePayloadPatch now provided by usePoiEditing hook (aliased from patchBasePayload)

  useEffect(() => {
    if (!visible) return;

    if (!activeCardId) {
      if (creatorInfo) setCreatorInfo(null);
      setFetchedCreatorId(null);
      return;
    }

    if (creatorInfo && creatorInfo.cardId === activeCardId) return;

    const sources = [
      normalizedCard,
      normalizedCard && normalizedCard.payload,
      normalizedCard && normalizedCard.rawMergedPayload,
      card,
      card && card.card,
      card && card.payload,
      rawData,
      rawData && rawData.rawMergedPayload,
    ];

    for (const src of sources) {
  const inline = pickInlineCreatorWrapped(src);
      if (inline) {
        setCreatorInfo(inline.cardId ? inline : { ...inline, cardId: activeCardId });
        setFetchedCreatorId(String(inline.id));
        return;
      }
    }

    const fallbackId = extractCreatorId(card) || extractCreatorId(normalizedCard) || null;
    if (fallbackId) {
      setFetchedCreatorId(String(fallbackId));
    } else {
      setFetchedCreatorId(null);
    }
    setCreatorInfo(null);
  }, [visible, activeCardId, normalizedCard, card, rawData, creatorInfo?.cardId]);

  useEffect(() => {
    let mounted = true;

    const loadCreatorInfo = async () => {
      if (!visible) return;
      // If we don't have a canonical id to fetch, consider content ready (new card / inline payload)
      if (!activeCardId) {
        // New card - no DB fetch needed, mark ready immediately
        console.log('[FullscreenPoiModal] No activeCardId (new card), setting dataReady=true');
        try { setDataReady(true); } catch (e) {}
        return;
      }
      if (!supabaseClient) {
        // No DB access - mark ready anyway
        console.log('[FullscreenPoiModal] No supabaseClient, setting dataReady=true');
        try { setDataReady(true); } catch (e) {}
        return;
      }
      if (creatorInfo && creatorInfo.cardId === activeCardId) return;

      try {
  console.log('[FullscreenPoiModal] Loading card from DB with ID:', activeCardId);

        // If we already have a creator id embedded in the incoming payload (e.g. list page provided it),
        // prefer using that immediately so we don't have to wait for a profiles query which causes
        // the ShareButton to appear late. This avoids a round-trip for the common case where the
        // list/listing already contained the creator id.
        if (extractedCreator && !creatorInfo) {
          try {
            // Check if we have inline creator profile info
            const hasInlineProfile = card && (card.creator_profile || card.creatorProfile || card.creator);
            
            if (hasInlineProfile) {
              // Use inline profile
              const profile = card.creator_profile || card.creatorProfile || card.creator;
              const fallbackName = profile?.display_name || profile?.nickname || profile?.name || 
                                   card.creator_display_name || card.creatorDisplayName || card.creator_name || '作者';
              setCreatorInfo({ 
                cardId: activeCardId, 
                id: String(extractedCreator), 
                display_name: fallbackName,
                avatar_url: profile?.avatar_url || profile?.avatarUrl,
                bio: profile?.bio,
              });
              setFetchedCreatorId(String(extractedCreator));
              try { setDataReady(true); } catch (e) {}
              return;
            } else {
              // No inline profile - fetch from profiles table
              console.log('[FullscreenPoiModal] Fetching creator profile for id:', extractedCreator);
              const { data: creatorData, error: creatorError } = await supabaseClient
                .from('profiles')
                .select('id, display_name, avatar_url, bio')
                .eq('id', extractedCreator)
                .single();
              
              if (!creatorError && creatorData && mounted) {
                setCreatorInfo({
                  cardId: activeCardId,
                  id: String(creatorData.id || extractedCreator),
                  display_name: creatorData.display_name || '作者',
                  avatar_url: creatorData.avatar_url,
                  bio: creatorData.bio,
                });
                setFetchedCreatorId(String(extractedCreator));
                try { setDataReady(true); } catch (e) {}
                return;
              } else {
                // Fetch failed - use fallback
                const fallbackName = card?.creator_display_name || card?.creatorDisplayName || card?.creator_name || '作者';
                setCreatorInfo({ cardId: activeCardId, id: String(extractedCreator), display_name: fallbackName });
                setFetchedCreatorId(String(extractedCreator));
                try { setDataReady(true); } catch (e) {}
                return;
              }
            }
          } catch (e) {
            // Fall through to normal DB load if anything fails here
            console.warn('[FullscreenPoiModal] Error handling extractedCreator:', e);
          }
        }

        const { data: fullCard, error: cardError } = await supabaseClient
          .from('cards_cards')
          .select('*')
          .eq('id', activeCardId)
          .single();

        if (cardError) {
          console.log('[FullscreenPoiModal] Card query error:', cardError);
          // If the card no longer exists (406 Not Acceptable for .single() with 0 rows),
          // treat as deleted: trigger upstream onDelete cleanup and close modal.
          const status = (cardError as any).status;
          const code = (cardError as any).code;
          if (status === 406 || code === 'PGRST116') {
            try { if (onDelete) await onDelete(); } catch (e) {}
            try { if (onClose) onClose(); } catch (e) {}
          }
          try { setDataReady(true); } catch (e) {}
          return;
        }

        if (!fullCard || !mounted) {
          try { setDataReady(true); } catch (e) {}
          return;
        }

  // Keep a copy of canonical row for ground-truth fields (e.g., latitude/longitude)
  try { setCanonicalCard(fullCard); } catch (e) {}

  const creatorId = fullCard.creator_id || fullCard.user_id || fullCard.created_by;
        if (!creatorId) {
          console.log('[FullscreenPoiModal] Card has no creator_id');
          // no creator to fetch, mark ready so UI can render without waiting forever
          try { setDataReady(true); } catch (e) {}
          return;
        }

        setFetchedCreatorId(String(creatorId));

        // Merge canonical DB row into local payload/view so freshly saved fields (e.g., website)
        // show up even if the opener passed a stale payload.
        try {
            if (!editMode) {
            const fromDb = (fullCard.base_payload && typeof fullCard.base_payload === 'object') ? fullCard.base_payload : {};
            setLocalBasePayload((prev: any) => {
              // If we had nothing yet, initialize from DB payload but prefer top-level canonical fields
              if (!prev || (typeof prev === 'object' && Object.keys(prev).length === 0)) {
                const base = clonePayload(fromDb) || {};
                // prefer top-level canonical fields from fullCard over base_payload
                try {
                  const canonicalKeys = ['title','description','address','website','phone','price_range','duration_minutes','opening_hours','tags','cover_image','latitude','longitude'];
                  for (const k of canonicalKeys) {
                    if (typeof (fullCard as any)[k] !== 'undefined' && (fullCard as any)[k] !== null && (fullCard as any)[k] !== '') {
                      (base as any)[k] = (fullCard as any)[k];
                    }
                  }
                } catch (e) {}
                return base;
              }
              // Otherwise, softly backfill important fields that are missing locally
              const next = clonePayload(prev) || {};
              const ensure = (key: string) => {
                const cur = next[key];
                if (cur === undefined || cur === null || cur === '') {
                  // prefer top-level canonical fields from fullCard first, then fall back to base_payload
                  if (typeof (fullCard as any)[key] !== 'undefined' && (fullCard as any)[key] !== null && (fullCard as any)[key] !== '') {
                    next[key] = (fullCard as any)[key];
                  } else if (fromDb && typeof fromDb[key] !== 'undefined' && fromDb[key] !== null && fromDb[key] !== '') {
                    next[key] = fromDb[key];
                  }
                }
              };
              ['website','phone','price_range','duration_minutes','opening_hours','title','description','address','tags','metaPairs',
               // lodging-specific fields to backfill from canonical row
               'starRating','hotelName','amenities','checkInTime','checkOutTime','platform_product_id','bookingTips']
               .forEach(ensure);
              return next;
            });
            // Sync top-level companions when empty
            if ((!localTags || localTags.length === 0) && Array.isArray(fullCard.tags)) {
              setLocalTags(fullCard.tags.slice());
            }
            if ((!localTitle || localTitle.length === 0) && (fullCard.title || fromDb.title)) {
              setLocalTitle(fullCard.title || fromDb.title || '');
            }
            if ((!localDescription || localDescription.length === 0) && (fullCard.description || fromDb.description)) {
              setLocalDescription(fullCard.description || fromDb.description || '');
            }
            if ((!localAddress || localAddress.length === 0) && (fullCard.address || fromDb.address)) {
              setLocalAddress(fullCard.address || fromDb.address || undefined);
            }
          }
        } catch (e) {
          // non-fatal
        }

        const { data: creatorData, error: creatorError } = await supabaseClient
          .from('profiles')
          .select('id, display_name, avatar_url, bio')
          .eq('id', creatorId)
          .single();

        if (creatorError) {
          console.log('[FullscreenPoiModal] Creator query error:', creatorError);
          return;
        }

        if (creatorData && mounted) {
          setCreatorInfo({
            cardId: activeCardId,
            id: String(creatorData.id || creatorId),
            display_name: creatorData.display_name || 'Unknown User',
            avatar_url: creatorData.avatar_url,
            bio: creatorData.bio,
          });
          // creator profile fetched and applied — mark data ready so the modal
          // (including ShareButton) renders only after we have creator info.
          try { setDataReady(true); } catch (e) {}
        }

  // Notify caller if needed
  try { if (onLoadedCanonical) onLoadedCanonical(fullCard); } catch (e) {}
      } catch (e) {
        console.warn('[FullscreenPoiModal] Error loading creator info:', e);
        try { setDataReady(true); } catch (err) {}
      }
    };

    loadCreatorInfo();
    return () => {
      mounted = false;
    };
  }, [visible, activeCardId, supabaseClient, creatorInfo?.cardId]);

  // Initialize localMeta ONCE when modal opens (visible changes from false to true)
  useEffect(() => {
    if (!visible) {
      prevVisible.current = false;
      localMetaInitializedRef.current = false; // Reset on close
      return;
    }
    
    // Only initialize on first open (prevVisible was false)
    if (prevVisible.current === true) {
      return; // Already open, skip all initialization
    }
    prevVisible.current = true;

    try {
      // Only start edit mode if explicitly requested via initialEditMode (for true placeholders)
      // Normal cards from itinerary should open in view mode even if they lack some IDs
      if (initialEditMode) {
        setEditMode(true);
      }
      // OSM flow or canonical card open will keep view mode until user taps Edit
    } catch (e) {}

    const pick = (obj: any, keys: string[]) => {
      if (!obj) return undefined;
      for (const k of keys) {
        if (k in obj && typeof obj[k] !== 'undefined' && obj[k] !== null) return obj[k];
      }
      return undefined;
    };

    const nestedPayload = (card && (card.payload || (card.card && card.card.payload))) || (normalizedCard && normalizedCard.payload) || undefined;

    const resolvedTitle = title
      || pick(normalizedCard, ['title', 'name', 'display_name', 'displayName', 'name_cn'])
      || pick(nestedPayload, ['title', 'name', 'display_name', 'displayName'])
      || pick(card && card.card, ['title', 'name'])
      || (rawData && rawData.rawMergedPayload && pick(rawData.rawMergedPayload, ['title', 'name']))
      || '';

    const resolvedDesc = description
      || pick(normalizedCard, ['description', 'summary', 'intro', 'desc'])
      || pick(nestedPayload, ['description', 'summary', 'intro', 'desc'])
      || pick(card && card.card, ['description'])
      || (rawData && rawData.rawMergedPayload && pick(rawData.rawMergedPayload, ['description', 'summary']))
      || '';

    const resolvedImage = imageUrl
      || pick(normalizedCard, ['imageUrl', 'image_url', 'cover_image', 'coverImage', 'image'])
      || pick(nestedPayload, ['imageUrl', 'image_url', 'cover_image', 'coverImage', 'image'])
      || pick(card && card.card, ['imageUrl', 'image_url', 'cover_image', 'image'])
      || (rawData && rawData.rawMergedPayload && pick(rawData.rawMergedPayload, ['imageUrl', 'image_url', 'cover_image', 'image']))
      || '';

    const resolvedAddress = address
      || pick(normalizedCard, ['address', 'location', 'location_name'])
      || pick(nestedPayload, ['address', 'location', 'location_name'])
      || pick(card && card.card, ['address', 'location'])
      || (rawData && rawData.rawMergedPayload && pick(rawData.rawMergedPayload, ['address', 'location']))
      || undefined;

    const payloadSourceCandidates = [
      normalizedCard && normalizedCard.base_payload,
      // Support itinerary item shape: card may actually be an itinerary item whose snapshot is in custom_payload
      card && card.custom_payload,
      card && card.base_payload,
      rawData && rawData.rawMergedPayload && rawData.rawMergedPayload.base_payload,
      nestedPayload && nestedPayload.base_payload,
      normalizedCard && normalizedCard.payload,
      card && card.payload,
      nestedPayload,
    ];

    let resolvedPayload: any = null;
    for (const candidate of payloadSourceCandidates) {
      if (candidate && typeof candidate === 'object') {
        resolvedPayload = candidate;
        break;
      }
    }

    if (resolvedPayload) {
      const payloadClone = clonePayload(resolvedPayload) || {};
      if (typeof resolvedTitle !== 'undefined' && resolvedTitle !== null) payloadClone.title = resolvedTitle;
      if (typeof resolvedDesc !== 'undefined' && resolvedDesc !== null) payloadClone.description = resolvedDesc;
      if (typeof resolvedAddress !== 'undefined' && resolvedAddress !== null) payloadClone.address = resolvedAddress;
      // Avoid overwriting locally edited/saved payload when toggling out of edit mode.
      // Only initialize when we don't yet have a payload.
      setLocalBasePayload((prev: any) => {
        if (!prev || (typeof prev === 'object' && Object.keys(prev).length === 0)) return payloadClone;
        return prev; // keep existing local payload (may contain freshly saved fields like website)
      });
    } else if (!editMode && (resolvedTitle || resolvedDesc || resolvedAddress)) {
      const fallbackPayload: any = {};
      if (resolvedTitle) fallbackPayload.title = resolvedTitle;
      if (resolvedDesc) fallbackPayload.description = resolvedDesc;
      if (resolvedAddress) fallbackPayload.address = resolvedAddress;
      setLocalBasePayload((prev: any) => {
        if (!prev || (typeof prev === 'object' && Object.keys(prev).length === 0)) return fallbackPayload;
        return prev;
      });
    }

    setLocalTitle(resolvedTitle);
    setLocalDescription(resolvedDesc);
    setLocalImage(resolvedImage || undefined);
    setLocalAddress(typeof resolvedAddress === 'string' ? resolvedAddress : (resolvedAddress && typeof resolvedAddress === 'object' ? JSON.stringify(resolvedAddress) : undefined));
      try {
      // Expand candidate locations for metaPairs so modal prefers authoritative snapshots.
      // IMPORTANT: prefer the incoming itinerary snapshot (props/meta or localBasePayload) first
      // when the modal is opened from an itinerary item. Only fall back to normalizedCard/cards_cards
      // when no itinerary-local snapshot is available.
      console.log('[FullscreenPoiModal] candidateMeta sources:', {
        metaData: metaData,
        'card.custom_payload.metaPairs': card?.custom_payload?.metaPairs,
        'localBasePayload.metaPairs': (localBasePayload as any)?.metaPairs,
        'rawData.metaPairs': rawData?.metaPairs,
        'rawData.rawMergedPayload.metaPairs': rawData?.rawMergedPayload?.metaPairs,
      });
      
      let candidateMeta = (
        // CRITICAL: For NEW cards (placeholders without card_id), ignore all existing metaPairs
        // to allow users to build their own field list from scratch
        initialEditMode && (card as any)?.meta?.isPlaceholder && !(card as any)?.card_id && !(card as any)?.custom_payload?.card_id
          ? []
          :
        // 1) props/meta passed by parent (this should be itinerary.custom_payload in itinerary flows)
        (Array.isArray(metaData) && metaData.length ? metaData :
          // 2) Itinerary item snapshot (card.custom_payload.metaPairs) — HIGHEST PRIORITY for行程条目
          (card && card.custom_payload && card.custom_payload.metaPairs) ||
          // 3) local base payload injected by parent/modal state
          (localBasePayload && Array.isArray((localBasePayload as any).metaPairs) && (localBasePayload as any).metaPairs.length ? (localBasePayload as any).metaPairs :
            // 4) fall back to normalizedCard / card.base_payload (cards_cards)
            ((normalizedCard && (normalizedCard.metaPairs || normalizedCard.metaData)) ||
            (normalizedCard && normalizedCard.base_payload && normalizedCard.base_payload.metaPairs) ||
            (normalizedCard && normalizedCard.payload && (normalizedCard.payload.metaPairs || normalizedCard.payload.metaData)) ||
            (nestedPayload && (nestedPayload.metaPairs || nestedPayload.metaData)) ||
            (nestedPayload && nestedPayload.base_payload && nestedPayload.base_payload.metaPairs) ||
            (card && (card.metaPairs || (card.base_payload && card.base_payload.metaPairs) || (card.payload && card.payload.metaPairs))) ||
            // CRITICAL: Check rawData.metaPairs directly (not just rawMergedPayload)
            (rawData && (rawData.metaPairs || (rawData.rawMergedPayload && (rawData.rawMergedPayload.metaPairs || rawData.rawMergedPayload.metaData)))) ||
            metaData))));
      
      console.log('[FullscreenPoiModal] resolved candidateMeta:', candidateMeta);
      
      // shopping类型强制补齐metaPairs结构 - DISABLED to let users add fields manually
      /*
      if ((normalizedCard && normalizedCard.card_type === 'shopping') && (!candidateMeta || candidateMeta.length < 10)) {
        try {
          const { createEmptyShoppingPayload } = require('../../../../module-travel/src/web/utils/emptyPayloads/shopping');
          candidateMeta = createEmptyShoppingPayload().metaPairs;
        } catch (e) {}
      }
      */
      
      // Initialize localMeta from candidateMeta
      if (Array.isArray(candidateMeta) && candidateMeta.length) {
        console.log('[FullscreenPoiModal] setting localMeta to authoritative snapshot:', {
          count: candidateMeta.length,
          sample: candidateMeta.slice(0, 5),
        });
        setLocalMeta(candidateMeta as any);
        localMetaInitializedRef.current = true;
      } else {
        // Fallback: inject placeholder metaPairs when creating a NEW card in edit mode.
        // Important: do NOT rely on activeCardId here because itinerary entry id may exist on card.id.
        const isPlaceholder = Boolean((card as any)?.meta?.isPlaceholder || (card as any)?._item?.meta?.isPlaceholder || (card as any)?.custom_payload?._fromPlaceholder);
        const linkedCardId = (card && (card as any).custom_payload && (((card as any).custom_payload.cardId) || ((card as any).custom_payload.card_id))) ? String(((card as any).custom_payload.cardId) || ((card as any).custom_payload.card_id)) : null;
        if (initialEditMode && (isPlaceholder || !linkedCardId)) {
          const kind = resolveKindForPlaceholders();
          // Do NOT auto-inject placeholder metaPairs - let users add fields via bottom buttons
          console.log('[FullscreenPoiModal] NOT injecting placeholder metaPairs for new card (user can add via buttons):', {
            kind
          });
          setLocalMeta([]);
          localMetaInitializedRef.current = true;
        } else {
          console.log('[FullscreenPoiModal] candidateMeta is empty or not an array; no injection. Signals:', { initialEditMode, activeCardId, isPlaceholder, linkedCardId });
        }
      }
      const candidateTags = (normalizedCard && normalizedCard.tags) || (nestedPayload && nestedPayload.tags) || (rawData && rawData.rawMergedPayload && rawData.rawMergedPayload.tags) || tags;
      if (Array.isArray(candidateTags) && candidateTags.length) setLocalTags(candidateTags as any);
    } catch (e) {}
    try {
      console.log('[FullscreenPoiModal] resolved fields (on open):', { resolvedTitle, resolvedDesc, resolvedImage, normalizedCard, nestedPayload, rawData });
    } catch (e) {}
  }, [visible]); // ONLY run when visible changes!

  // When the fullscreen modal is visible on web, hide native scrollbars visually
  // while keeping scroll behavior active (mouse wheel / touch still scrolls).
  useEffect(() => {
    if (typeof document === 'undefined') return;
    try {
      if (visible) document.body.classList.add('hide-scrollbar');
      else document.body.classList.remove('hide-scrollbar');
    } catch (e) {}
    return () => {
      try { document.body.classList.remove('hide-scrollbar'); } catch (e) {}
    };
  }, [visible]);

  // Debug: when modal becomes visible, log incoming props to help diagnose missing fields
  useEffect(() => {
    try {
      if (visible) {
        // eslint-disable-next-line no-console
        console.log('[FullscreenPoiModal] visible payload:', { card, title, description, canEdit, isOwner, currentUserId });
      }
    } catch (e) {}
  }, [visible, card, title, description, canEdit, isOwner, currentUserId]);

  // Compute bottom navigation inset on web so content won't be hidden behind app bottom nav
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') return undefined;
    let mounted = true;
    const computeInset = () => {
      try {
        let navEl: Element | null = document.getElementById('app-bottom-nav-portal');
        if (navEl && navEl.children && navEl.children.length === 1) {
          navEl = navEl.children[0] as Element;
        }
        if (!navEl) {
          const fixedEls = Array.from(document.querySelectorAll('*')).filter((e) => getComputedStyle(e as Element).position === 'fixed');
          navEl = fixedEls.find((e) => {
            try {
              const r = (e as Element).getBoundingClientRect();
              return Math.abs(r.bottom - window.innerHeight) < 8 && r.height > 20;
            } catch (e) { return false; }
          }) || null;
        }
        if (!mounted) return;
        if (navEl) {
          const rect = navEl.getBoundingClientRect();
          const inset = Math.ceil(rect.height + 12);
          setBottomInset(inset);
          return;
        }
      } catch (e) {
        // ignore
      }
      if (mounted) setBottomInset(250);
    };
    window.addEventListener('resize', computeInset);
    const mo = new MutationObserver(computeInset);
    try { mo.observe(document.body, { childList: true, subtree: true }); } catch (e) {}
    computeInset();
    return () => {
      mounted = false;
      window.removeEventListener('resize', computeInset);
      try { mo.disconnect(); } catch (e) {}
    };
  }, []);

  // Centralized navigate handler used by the title/address section
  const handleNavigate = useCallback(() => {
    const base: any = basePayloadRef.current || localBasePayload || {};
    const location: any = (normalizedCard && (normalizedCard.location || normalizedCard.base_payload?.location)) || base.location;
    const resolveCoords = (): { lat: number; lng: number } | null => {
      const readNumber = (value: any): number | null => {
        const num = Number(value);
        return Number.isFinite(num) ? num : null;
      };
      const scan = (src: any, depth = 0): { lat: number; lng: number } | null => {
        if (!src || typeof src !== 'object' || depth > 3) return null;
        const latKeys = ['lat', 'latitude', 'Lat', 'Latitude', 'y', 'Y'];
        const lngKeys = ['lng', 'longitude', 'lon', 'Lon', 'long', 'Long', 'x', 'X'];
        let latCandidate: number | null = null;
        let lngCandidate: number | null = null;
        for (const key of latKeys) {
          if (Object.prototype.hasOwnProperty.call(src, key)) {
            const maybe = readNumber((src as any)[key]);
            if (maybe !== null) { latCandidate = maybe; break; }
          }
        }
        for (const key of lngKeys) {
          if (Object.prototype.hasOwnProperty.call(src, key)) {
            const maybe = readNumber((src as any)[key]);
            if (maybe !== null) { lngCandidate = maybe; break; }
          }
        }
        if (latCandidate !== null && lngCandidate !== null) { return { lat: latCandidate, lng: lngCandidate }; }
        const coordsArray = (src as any).coordinates;
        if (Array.isArray(coordsArray) && coordsArray.length >= 2) {
          const first = readNumber(coordsArray[0]);
          const second = readNumber(coordsArray[1]);
          if (first !== null && second !== null) {
            const candidateA = { lat: first, lng: second };
            const candidateB = { lat: second, lng: first };
            if (Math.abs(candidateA.lat) <= 90 && Math.abs(candidateA.lng) <= 180) return candidateA;
            if (Math.abs(candidateB.lat) <= 90 && Math.abs(candidateB.lng) <= 180) return candidateB;
          }
        }
        const nestedKeys = ['location', 'loc', 'geo', 'center'];
        for (const key of nestedKeys) {
          if (Object.prototype.hasOwnProperty.call(src, key) && src[key] && typeof src[key] === 'object') {
            const nested = scan(src[key], depth + 1);
            if (nested) return nested;
          }
        }
        return null;
      };
      const sources = [
        base,
        base?.location,
        localBasePayload,
        localBasePayload?.location,
        normalizedCard,
        normalizedCard?.base_payload,
        normalizedCard?.payload,
        normalizedCard?.location,
        card,
        card?.payload,
        card?.location,
        rawData,
        rawData?.rawMergedPayload,
        rawData?.rawMergedPayload?.location,
      ];
      for (const source of sources) {
        const result = scan(source);
        if (result) return result;
      }
      return null;
    };

    const coords = resolveCoords();
    const navAddress = (() => {
      const primary = firstAddressFrom(
        localAddress,
        address,
        base?.address,
        localBasePayload?.address,
        base?.location_name,
        localBasePayload?.location_name,
        card && (card as any).address,
        card && (card as any).location_name,
        card && (card as any).custom_payload && (card as any).custom_payload.address,
        card && (card as any).custom_payload && (card as any).custom_payload.location_name,
        card && (card as any).inline_payload && (card as any).inline_payload.address,
        card && (card as any).inline_payload && (card as any).inline_payload.location_name,
        card && (card as any).card && (card as any).card.address,
        card && (card as any).card && (card as any).card.location_name,
        normalizedCard && (normalizedCard as any).address,
        normalizedCard && normalizedCard.base_payload && (normalizedCard.base_payload as any).address,
        normalizedCard && normalizedCard.base_payload && (normalizedCard.base_payload as any).location_name,
        normalizedCard && normalizedCard.payload && (normalizedCard.payload as any).address,
        normalizedCard && normalizedCard.payload && (normalizedCard.payload as any).location_name,
        rawData && (rawData as any).address,
        rawData && rawData.rawMergedPayload && (rawData.rawMergedPayload as any).address
      );
      if (primary) return primary;
      const metaCandidate = extractAddressFromMeta(localMeta)
        || extractAddressFromMeta(metaData)
        || extractAddressFromMeta((card && (card as any).metaPairs) ? (card as any).metaPairs : undefined)
        || extractAddressFromMeta((card && (card as any).meta) ? (card as any).meta : undefined)
        || extractAddressFromMeta((card && (card as any).custom_payload && (card as any).custom_payload.metaPairs) ? (card as any).custom_payload.metaPairs : undefined)
        || extractAddressFromMeta((normalizedCard && (normalizedCard as any).metaPairs) ? (normalizedCard as any).metaPairs : undefined)
        || extractAddressFromMeta((normalizedCard && normalizedCard.base_payload && Array.isArray((normalizedCard.base_payload as any).metaPairs) ? (normalizedCard.base_payload as any).metaPairs : undefined))
        || extractAddressFromMeta((localBasePayload && Array.isArray((localBasePayload as any).metaPairs) ? (localBasePayload as any).metaPairs : undefined))
        || extractAddressFromMeta((base && Array.isArray((base as any).metaPairs) ? (base as any).metaPairs : undefined))
        || extractAddressFromMeta((rawData && rawData.rawMergedPayload && Array.isArray((rawData.rawMergedPayload as any).metaPairs) ? (rawData.rawMergedPayload as any).metaPairs : undefined));
      if (metaCandidate) return metaCandidate;
      const locationCandidate = firstAddressFrom(
        location,
        base?.location,
        localBasePayload?.location,
        card && (card as any).location,
        card && (card as any).custom_payload && (card as any).custom_payload.location,
        card && (card as any).inline_payload && (card as any).inline_payload.location,
        card && (card as any).card && (card as any).card.location,
        normalizedCard && (normalizedCard as any).location,
        normalizedCard && normalizedCard.base_payload && (normalizedCard.base_payload as any).location,
        normalizedCard && normalizedCard.payload && (normalizedCard.payload as any).location,
        rawData && (rawData as any).location,
        rawData && rawData.rawMergedPayload && (rawData.rawMergedPayload as any).location
      );
      if (locationCandidate) return locationCandidate;
      try {
        if (card && card.payload && typeof card.payload === 'object') {
          const payloadAddress = firstAddressFrom(
            (card.payload as any).address,
            (card.payload as any).location,
            (card.payload as any).fullAddress
          );
          if (payloadAddress) return payloadAddress;
        }
      } catch {}
      return undefined;
    })();

    if (typeof onRequestMapNavigation === 'function' && (navAddress || coords)) {
      try {
        onRequestMapNavigation({
          address: navAddress,
          coords: coords ? { lat: coords.lat, lng: coords.lng } : null,
          title: localTitle || title,
          location,
          card: normalizedCard || card,
        });
        return;
      } catch (err) {
        console.warn('[FullscreenPoiModal] onRequestMapNavigation threw', err);
      }
    }
    const map = buildMapUrl({ title: localTitle || title, address: navAddress, lat: coords?.lat, lng: coords?.lng, location }, Platform.OS);
    const url = (Platform.OS !== 'web' && map?.deep) ? map?.deep : map?.web;
    if (!url) return;
    if (typeof window !== 'undefined') window.open(url, '_blank');
    else if (Linking && Linking.openURL) Linking.openURL(url);
  }, [basePayloadRef, localBasePayload, normalizedCard, card, rawData, localAddress, address, localMeta, metaData, onRequestMapNavigation, localTitle, title]);
  // If the modal is opened but canonical data isn't loaded yet, render a blocking
  // fullscreen loader and do NOT render the modal content. This prevents the
  // partial-content flicker (ShareButton/creator appearing later).
  // Web: use createPortal to render at document.body level
  if (Platform.OS === 'web' && visible && !dataReady) {
    return ReactDOM.createPortal(
      <SafeView style={styles.webOverlay}>
        <SafeView style={[styles.webContent, { alignItems: 'center', justifyContent: 'center' }]}>
          <ActivityIndicator size="large" color="#0ea5a4" />
          <Text style={styles.loadingText}>正在加載卡片詳情...</Text>
        </SafeView>
      </SafeView>,
      document.body
    );
  }

  const imageAttributions: string[] | null = (() => {
      try {
        const candidates = [
          card && card.custom_payload && card.custom_payload.image_attributions,
          localBasePayload && localBasePayload.image_attributions,
          normalizedCard && normalizedCard.base_payload && normalizedCard.base_payload.image_attributions,
          normalizedCard && normalizedCard.image_attributions,
          card && card.image_attributions,
        ];
        for (const c of candidates) {
          if (Array.isArray(c) && c.length) return c as string[];
        }
      } catch (e) {}
      return null;
    })();

  const mainContent = (
      <View style={styles.container}>
        {/* Hidden file input for cover image upload */}
        {Platform.OS === 'web' && (
          <input
            ref={fileInputRef as any}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target?.files?.[0];
              console.log('[FullscreenPoiModal] File selected:', file);
              if (file) {
                onCoverFileSelected(file);
              }
            }}
          />
        )}
        <ScrollView
          className="hide-scrollbar"
          style={styles.scroll}
          contentContainerStyle={{ paddingBottom: bottomInset, paddingTop: 0, flexGrow: 1 }}
        >
          <CenteredContentWrapper maxWidth={600} paddingHorizontal={0}>
            <View style={[styles.headerContainer, isWide ? { height: 220 } : { aspectRatio: 16 / 9 }]}>
              {/* 背景層：優先檢查是否有圖片，無圖片則顯示地圖（OSM POI 專用） */}
              {(() => {
                // 更嚴格地檢查是否已上傳/存在真實圖片（排除空字符串、placeholder、undefined）
                const rawImage = localImage ||
                  imageUrl ||
                  (cardForRender && (cardForRender.imageUrl || cardForRender.image_url || cardForRender.cover_image || cardForRender.coverImage || cardForRender.image)) ||
                  '';
                
                // 只有當圖片 URL 不為空且不包含 placeholder 路徑時，才算「有圖片」
                const hasRealImage = !!(
                  rawImage &&
                  typeof rawImage === 'string' &&
                  rawImage.trim().length > 0 &&
                  !rawImage.includes('placeholder') &&
                  !rawImage.includes('default')
                );

                // Check if this is an embed card (external type with embed.src)
                const embedData = 
                  (normalizedCard && normalizedCard.embed) ||
                  (normalizedCard && normalizedCard.base_payload && normalizedCard.base_payload.embed) ||
                  (normalizedCard && normalizedCard.payload && normalizedCard.payload.embed) ||
                  (normalizedCard && normalizedCard.custom_payload && normalizedCard.custom_payload.embed) ||
                  (cardForRender && cardForRender.embed) ||
                  (cardForRender && cardForRender.base_payload && cardForRender.base_payload.embed) ||
                  (cardForRender && cardForRender.payload && cardForRender.payload.embed) ||
                  (cardForRender && cardForRender.custom_payload && cardForRender.custom_payload.embed) ||
                  (localBasePayload && localBasePayload.embed) ||
                  null;
                const hasEmbed = !!(embedData && embedData.src);

                console.log('[FullscreenPoiModal] Header render decision:', {
                  hasRealImage,
                  hasEmbed,
                  embedSrc: embedData && embedData.src,
                  rawImage,
                  nearbyCoords,
                  willShowMap: !hasRealImage && !hasEmbed && Platform.OS === 'web' && !!nearbyCoords,
                });

                if (hasEmbed && embedData && embedData.src) {
                  // 情況0：有 embed → 顯示 iframe 播放器
                  return (
                    <View style={styles.headerImageWrapper}>
                      {/* @ts-ignore */}
                      <iframe
                        src={embedData.src}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          border: 'none',
                        }}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                      {/* Overlay to allow header buttons to be clickable above iframe */}
                      <View style={[styles.headerOverlay, { pointerEvents: 'none' }]} />
                    </View>
                  );
                } else if (hasRealImage) {
                  // 情況1：有真實圖片 → 顯示圖片
                  return (
                    <View style={styles.headerImageWrapper}>
                      {/* @ts-ignore */}
                      <img src={rawImage} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      <View style={styles.headerOverlay} />
                                       </View>
                  );
                } else if (Platform.OS === 'web' && nearbyCoords) {
                  // 情況2：無圖片 + 有坐標（OSM POI）→ 顯示地圖 + marker
                  return (
                    <View style={styles.headerMapWrapper}>
                      <MapContainer
                        center={[nearbyCoords.lat, nearbyCoords.lng]}
                        zoom={17}
                        style={{ width: '100%', height: '100%' }}
                        scrollWheelZoom={false}
                        dragging={false}
                        doubleClickZoom={false}
                        zoomControl={false}
                        attributionControl={false}
                      >
                        <TileLayer
                          attribution="© OpenStreetMap contributors"
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        <Marker
                          position={[nearbyCoords.lat, nearbyCoords.lng]}
                          icon={L.divIcon({
                            className: '',
                            html: `
                              <div style="width:28px;height:28px;border-radius:9999px;background:#F97316;display:flex;align-items:center;justify-content:center;box-shadow:0 6px 16px rgba(15,23,42,0.4);border:3px solid #fff;">
                                <span class="material-icons" style="font-size:18px;color:#fff;line-height:1;">place</span>
                              </div>
                            `,
                            iconSize: [28, 28],
                            iconAnchor: [14, 28],
                          })}
                        />
                      </MapContainer>
                      <View style={styles.headerMapOverlay} pointerEvents="none" />
                    </View>
                  );
                } else {
                  // 情況3：無圖片也無坐標 → 灰色占位
                  return <View style={[styles.headerImageWrapper, { backgroundColor: '#E5E7EB' }]} />;
                }
              })()}

              {/* 控制按鈕層：永遠顯示在最上層 */}
              <View style={styles.headerControlsOverlay} pointerEvents="box-none">
                <View style={styles.headerTopRow} pointerEvents="box-none">
                  {/* 左側：返回按鈕 */}
                  <TouchableOpacity style={styles.headerIconButton} onPress={onClose}>
                    <PlatformIcon name="arrow-back" size={24} color="#fff" />
                  </TouchableOpacity>

                  {/* 右側：編輯/保存/刪除按鈕 */}
                  <View style={styles.headerRightButtons} pointerEvents="box-none">
                    {editMode ? (
                      <>
                        {/* 編輯模式：顯示上傳圖片（或嵌入代碼）、保存、取消 */}
                        {(() => {
                          // 檢測是否為 video 卡片
                          // 擴展判斷邏輯：如果初始類型是 video，或者已經有 embed 內容，即使當前選了其他類型（如餐廳），也保持 video 編輯模式
                          const isVideoCard = derivedCardType === 'video' || 
                            (localBasePayload && localBasePayload.card_type === 'video') ||
                            (localTitle && localTitle.includes('視頻分享')) ||
                            (localBasePayload && localBasePayload.embed) ||
                            (incomingTypeHint === 'video');
                          
                          const isExternalLink = derivedCardType === 'external' || 
                            (localBasePayload && localBasePayload.card_type === 'external') ||
                            (incomingTypeHint === 'external');
                          
                          if (isVideoCard) {
                            // Video 卡片：顯示嵌入 icon
                            return (
                              <TouchableOpacity
                                style={styles.headerIconButton}
                                onPress={() => {
                                  console.log('[FullscreenPoiModal] Embed button clicked');
                                  setShowEmbedModal(true);
                                }}
                              >
                                <Text style={{ color: '#fff', fontSize: 20, fontWeight: 'bold' }}>{'< >'}</Text>
                              </TouchableOpacity>
                            );
                          } else if (isExternalLink) {
                            // 外部链接卡片：显示曲别针 icon
                            return (
                              <TouchableOpacity
                                style={styles.headerIconButton}
                                onPress={() => {
                                  console.log('[FullscreenPoiModal] Link button clicked');
                                  setShowEmbedModal(true);
                                }}
                              >
                                <PlatformIcon name="attach_file" size={24} color="#fff" family="MaterialIcons" />
                              </TouchableOpacity>
                            );
                          } else {
                            // 其他卡片：顯示相機 icon
                            return (
                              <TouchableOpacity
                                style={styles.headerIconButton}
                                onPress={() => {
                                  console.log('[FullscreenPoiModal] Camera button clicked');
                                  if (fileInputRef.current) {
                                    fileInputRef.current.click();
                                  }
                                }}
                              >
                                <PlatformIcon name="photo_camera" size={24} color="#fff" family="MaterialIcons" />
                              </TouchableOpacity>
                            );
                          }
                        })()}

                        {saving ? (
                          <View style={styles.headerIconButton}>
                            <ActivityIndicator size="small" color="#fff" />
                          </View>
                        ) : (
                          <TouchableOpacity style={styles.headerIconButton} onPress={save}>
                            <PlatformIcon name="done" size={24} color="#fff" family="MaterialIcons" />
                          </TouchableOpacity>
                        )}
                      </>
                    ) : (
                      <>
                        {/* 瀏覽模式：顯示編輯按鈕 */}
                        {(canEdit || isOwner || initialEditMode) && (
                          <TouchableOpacity style={styles.headerIconButton} onPress={startEdit}>
                            <PlatformIcon name="edit" size={24} color="#fff" />
                          </TouchableOpacity>
                        )}
                      </>
                    )}
                  </View>
                </View>

                {/* 圖片來源標注（如果有） */}
                {imageAttributions && imageAttributions.length > 0 && (
                  <View style={styles.attributionBadge}>
                    <AttributionLine html={imageAttributions[0]} />
                  </View>
                )}
              </View>
            </View>
          </CenteredContentWrapper>

          <CenteredContentWrapper maxWidth={600} paddingHorizontal={24}>
            {(() => {
              // Only show ShareSection when not editing AND we are viewing a real card (has id)
              // and this is not an itinerary placeholder.
              const isPlaceholder = Boolean((card as any)?.meta?.isPlaceholder || (card as any)?._item?.meta?.isPlaceholder);
              const hasCanonicalId = Boolean(createdCardId || normalizedCard?.id || card?.id || card?.cardId || card?.card_id);
              const showShare = !editMode && hasCanonicalId && !isPlaceholder;
              if (!showShare) return null;
              return (
              <ShareSection
                visible={visible}
                dataReady={dataReady}
                cardId={(createdCardId || normalizedCard?.id || card?.id || card?.cardId || card?.card_id) ? String(createdCardId || normalizedCard?.id || card?.id || card?.cardId || card?.card_id) : null}
                title={localTitle || title || ''}
                description={localDescription || description || ''}
                imageUrl={localImage || imageUrl || undefined}
                creator={shareCreator as any}
                onCreatorPress={onCreatorPress}
              />
              );
            })()}
          </CenteredContentWrapper>

          <CenteredContentWrapper maxWidth={600} paddingHorizontal={24}>
          <View style={[styles.content, { paddingHorizontal: 24 }]}> 
            {/* DEBUG: show resolved sources when running on localhost to help trace missing fields */}
            {/* This debug block is now removed as requested.
            {typeof window !== 'undefined' && window.location && String(window.location.host).includes('localhost') ? (
              <View style={styles.debugBlock}>
                <Text style={styles.debugKey}>来源检查（local / top-level / base_payload）</Text>
                {(() => {
                  try {
                    const keys = ['title','address','website','phone','price_range'];
                    const rows: Array<any> = keys.map(k => {
                      const top = (fullCard && (fullCard as any)[k]) || undefined;
                      const base = (fullCard && fullCard.base_payload && (fullCard.base_payload as any)[k]) || undefined;
                      const local = (localBasePayload && (localBasePayload as any)[k]) || undefined;
                      return { key: k, top: top, base: base, local: local };
                    });
                    return rows.map(r => (
                      <View key={r.key} style={styles.debugRow}>
                        <Text style={styles.debugKey}>{r.key}</Text>
                        <Text style={styles.debugVal}>top: {String(r.top || '—')}</Text>
                        <Text style={styles.debugVal}>base: {String(r.base || '—')}</Text>
                        <Text style={styles.debugVal}>local: {String(r.local || '—')}</Text>
                      </View>
                    ));
                  } catch (e) { return <Text style={styles.debugVal}>debug error</Text>; }
                })()}
              </View>
            ) : null}
            */}
            {/* Diagnostic: show exactly what we're about to hand to the modal content */}
            {(() => {
              try {
                console.log('[FullscreenPoiModal] passing to FullscreenPoiModalContent', {
                  title: title,
                  localTitle: localTitle,
                  address: localAddress,
                  description: localDescription || description || '',
                  meta: localMeta,
                  tags: localTags,
                  cardPayload: localBasePayload,
                  cardType: derivedCardType,
                  cardId: createdCardId || normalizedCard?.id || card?.id || card?.cardId || card?.card_id,
                });
              } catch (e) {}
              return null;
            })()}

            {/* Title + Address (inline editable) */}
            <PoiTitleAddressSection
              editMode={editMode}
              localTitle={localTitle}
              onChangeTitle={setLocalTitle}
              titleFallback={title}
              placeholderTitle={(() => {
                const v = String((hookDerivedCardType || derivedCardType || incomingTypeHint || 'poi')).toLowerCase();
                if (v === 'restaurant' || v === 'meal' || v === 'food') return '請輸入餐廳名稱';
                if (v === 'lodging' || v === 'hotel') return '請輸入住宿名稱';
                if (v === 'shopping' || v === 'shop' || v === 'mall') return '請輸入購物點名稱';
                if (v === 'activity' || v === 'event') return '請輸入活動名稱';
                return '請輸入景點名稱';
              })()}
              localAddress={typeof localAddress === 'string' ? localAddress : ''}
              onChangeAddress={setLocalAddress}
              onNavigate={handleNavigate}
            />

            {/* Card Type Selector (Edit Mode Only) */}
            {editMode && (
              <CardTypeSelector
                currentType={derivedCardType}
                onSelect={(type) => {
                  console.log('[FullscreenPoiModal] Selected card type:', type);
                  handleBasePayloadPatch({ card_type: type });
                }}
              />
            )}

            {/* Tabs for Details / Description */}
            <View style={[styles.section, { marginTop: 16 }]}>
              <View style={styles.tabsRow}>
                {[
                  { key: 'details', label: '详细信息' },
                  nearbyCoords ? { key: 'nearby', label: '附近景点' } : null,
                  { key: 'description', label: '描述' },
                  { key: 'booking', label: '预定' },
                ].filter(Boolean).map((tab: any) => {
                  const isActive = activeTab === tab.key;
                  return (
                    <TouchableOpacity
                      key={tab.key}
                      style={[styles.tabBtn, isActive && styles.tabBtnActive]}
                      onPress={() => setActiveTab(tab.key)}
                    >
                      <Text style={[styles.tabText, isActive && styles.tabTextActive]}>{tab.label}</Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
              <View style={styles.tabDivider} />
              {activeTab === 'details' && (
                <DetailsTab
                  meta={localMeta}
                  editMode={editMode}
                  displayedMeta={editMode ? localMeta : displayedMeta}
                  setLocalMeta={setLocalMetaSafe}
                  tags={localTags}
                  setTags={setLocalTags}
                  coordinates={nearbyCoords}
                  onCoordinatesChange={(lat, lng) => {
                    handleBasePayloadPatch({ latitude: lat, longitude: lng });
                  }}
                />
              )}
              {activeTab === 'nearby' && (
                <NearbyTab
                  supabaseClient={supabaseClient}
                  cardId={createdCardId || normalizedCard?.id || card?.id || card?.cardId || card?.card_id || null}
                  coords={nearbyCoords}
                  title={localTitle || title}
                  address={localAddress || address}
                  primaryImageUrl={localImage || imageUrl}
                  onSelectNearby={(spotId) => {
                    // Open external card detail page in new tab (web only) or noop.
                    try {
                      if (spotId && typeof window !== 'undefined') {
                        window.open(`/card/${spotId}?from=modal`, '_blank');
                      }
                    } catch (e) {}
                  }}
                />
              )}
              {activeTab === 'description' && (
                <DescriptionTab
                  editMode={editMode}
                  value={localDescription}
                  fallback={description || ''}
                  onChange={setLocalDescription}
                />
              )}
              {activeTab === 'booking' && (
                <BookingTab
                  supabaseClient={supabaseClient}
                  extractCardId={extractCardId}
                  uploadCardType={uploadCardType}
                  basePayloadRef={basePayloadRef}
                  localBasePayload={localBasePayload}
                  normalizedCard={normalizedCard}
                />
              )}
            </View>

            {/* Original complex modal content - HIDDEN for now */}
            {false && (
            <FullscreenPoiModalContent
              title={title}
              address={localAddress}
              description={localDescription || description || ''}
              meta={localMeta}
              tags={localTags}
              rawData={rawData}
              cardPayload={localBasePayload}
              cardType={derivedCardType}
              editMode={editMode}
              localTitle={localTitle}
              localDescription={localDescription}
              onChangeTitle={setLocalTitle}
              onChangeDescription={setLocalDescription}
              onChangeAddress={(value) => setLocalAddress(value)}
              onChangeMeta={(next) => setLocalMeta(next)}
              onChangeTags={(next) => setLocalTags(next)}
              onChangePayload={handleBasePayloadPatch}
              editableInline={editMode}
              supabaseClient={supabaseClient}
              cardId={createdCardId || normalizedCard?.id || card?.id || card?.cardId || card?.card_id}
            />
            )}

            {(card && (card.last_edited_by || card.last_edited_at)) ? (
              <View style={[styles.section, { marginTop: 12 }]}> 
                <Text style={{ color: '#6B7280', fontSize: 13 }}>最後編輯：{card.last_edited_by || ''} {card.last_edited_at ? ` • ${card.last_edited_at}` : ''}</Text>
              </View>
            ) : null}
          </View>
          </CenteredContentWrapper>
          {/* Extra tail spacer to ensure last rows can scroll above any fixed UI */}
          <View style={{ height: Math.max(40, bottomInset + 80) }} />
        </ScrollView>

        {/* Embed Input Modal */}
        {showEmbedModal && (
          <View style={styles.embedModalOverlay}>
            <View style={[styles.embedModalContent, (derivedCardType === 'external' || incomingTypeHint === 'external') ? { maxWidth: 600 } : {}]}>
              <Text style={styles.embedModalTitle}>
                {(derivedCardType === 'external' || incomingTypeHint === 'external') ? '粘贴网址并生成预览' : '嵌入視頻'}
              </Text>
              
              {(derivedCardType === 'external' || incomingTypeHint === 'external') ? (
                // External Link UI
                <>
                  <TextInput
                    style={[styles.embedModalInput, { minHeight: 48, maxHeight: 48, paddingVertical: 0, justifyContent: 'center' }]}
                    placeholder="請輸入網址 (https://...)"
                    value={embedInput}
                    onChangeText={setEmbedInput}
                    autoFocus
                    multiline={false}
                    onSubmitEditing={() => {
                      if (parsedMeta && !parsedMeta.error) {
                        // Add
                        if (parsedMeta.title) setLocalTitle(parsedMeta.title);
                        if (parsedMeta.description) setLocalDescription(parsedMeta.description);
                        if (parsedMeta.image) setLocalImage(parsedMeta.image);
                        handleBasePayloadPatch({
                          url: parsedMeta.resolvedUrl || parsedMeta.url,
                          siteName: parsedMeta.siteName,
                          favicon: parsedMeta.favicon,
                          imageUrl: parsedMeta.image,
                          description: parsedMeta.description,
                          partner: parsedMeta.partner,
                          embed: parsedMeta.embed,
                          type: 'external',
                          card_type: 'external'
                        });
                        setShowEmbedModal(false);
                        setEmbedInput('');
                        setParsedMeta(null);
                      } else {
                        // Parse
                        extractMeta(embedInput);
                      }
                    }}
                  />
                  
                  <View style={{ flexDirection: 'row', gap: 12, marginBottom: 20 }}>
                    <TouchableOpacity
                      style={[styles.embedModalButton, { backgroundColor: '#fff', borderWidth: 1, borderColor: '#FB7A4A', flex: 1 }]}
                      onPress={() => extractMeta(embedInput)}
                      disabled={parsingUrl}
                    >
                      <Text style={{ color: '#FB7A4A', fontSize: 16, fontWeight: '600' }}>
                        {parsingUrl ? '解析中...' : '解析網址'}
                      </Text>
                    </TouchableOpacity>
                    
                    <TouchableOpacity
                      style={[styles.embedModalButton, { backgroundColor: '#FB7A4A', flex: 1, opacity: (parsedMeta && !parsedMeta.error) ? 1 : 0.5 }]}
                      onPress={() => {
                        if (parsedMeta && !parsedMeta.error) {
                          if (parsedMeta.title) setLocalTitle(parsedMeta.title);
                          if (parsedMeta.description) setLocalDescription(parsedMeta.description);
                          if (parsedMeta.image) setLocalImage(parsedMeta.image);
                          handleBasePayloadPatch({
                            url: parsedMeta.resolvedUrl || parsedMeta.url,
                            siteName: parsedMeta.siteName,
                            favicon: parsedMeta.favicon,
                            imageUrl: parsedMeta.image,
                            description: parsedMeta.description,
                            partner: parsedMeta.partner,
                            embed: parsedMeta.embed,
                            type: 'external',
                            card_type: 'external'
                          });
                          setShowEmbedModal(false);
                          setEmbedInput('');
                          setParsedMeta(null);
                        }
                      }}
                      disabled={!parsedMeta || !!parsedMeta.error}
                    >
                      <Text style={styles.embedModalConfirmText}>加入此網址</Text>
                    </TouchableOpacity>
                  </View>

                  {/* Preview Card */}
                  {parsedMeta && !parsedMeta.error && (
                    <View style={{ borderWidth: 1, borderColor: '#E5E7EB', borderRadius: 8, overflow: 'hidden', marginBottom: 12 }}>
                      <View style={{ flexDirection: 'row', padding: 12, gap: 12 }}>
                        {parsedMeta.image && (
                          // @ts-ignore
                          <img src={parsedMeta.image} style={{ width: 80, height: 80, borderRadius: 4, objectFit: 'cover' }} />
                        )}
                        <View style={{ flex: 1 }}>
                          <Text style={{ fontSize: 14, fontWeight: '700', color: '#111', marginBottom: 4 }} numberOfLines={2}>{parsedMeta.title}</Text>
                          <Text style={{ fontSize: 12, color: '#666', marginBottom: 4 }} numberOfLines={2}>{parsedMeta.description}</Text>
                          <Text style={{ fontSize: 11, color: '#999' }}>{parsedMeta.siteName || 'External Link'}</Text>
                        </View>
                      </View>
                      <View style={{ backgroundColor: '#F9FAFB', padding: 8, borderTopWidth: 1, borderTopColor: '#E5E7EB' }}>
                        <Text style={{ fontSize: 12, color: '#6B7280', textAlign: 'center' }}>
                          已解析成功：点击「加入此网址」保存，或直接按 Enter 键快速保存。
                        </Text>
                      </View>
                    </View>
                  )}
                  
                  {/* Error Message */}
                  {parsedMeta && parsedMeta.error && (
                     <Text style={{ color: 'red', marginBottom: 12, textAlign: 'center' }}>解析失敗，請檢查網址或稍後重試</Text>
                  )}
                  
                  {/* Close Button for External Mode */}
                  <TouchableOpacity
                    style={{ position: 'absolute', top: -10, right: -10, padding: 10 }}
                    onPress={() => {
                      setShowEmbedModal(false);
                      setEmbedInput('');
                      setParsedMeta(null);
                    }}
                  >
                    <Text style={{ fontSize: 20, color: '#999' }}>✕</Text>
                  </TouchableOpacity>

                </>
              ) : (
                // Video Embed UI (Original)
                <>
                  <TextInput
                    style={styles.embedModalInput}
                    placeholder="請粘貼視頻嵌入代碼（YouTube, Bilibili...）"
                    value={embedInput}
                    onChangeText={setEmbedInput}
                    autoFocus
                    multiline={true}
                    numberOfLines={4}
                    textAlignVertical="top"
                  />
                  <View style={styles.embedModalButtons}>
                    <TouchableOpacity
                      style={[styles.embedModalButton, styles.embedModalCancelButton]}
                      onPress={() => {
                        setShowEmbedModal(false);
                        setEmbedInput('');
                        setParsedMeta(null);
                      }}
                    >
                      <Text style={styles.embedModalCancelText}>取消</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[styles.embedModalButton, styles.embedModalConfirmButton]}
                      onPress={() => {
                         handleEmbedInput(embedInput);
                      }}
                      disabled={parsingUrl}
                    >
                      <Text style={styles.embedModalConfirmText}>{parsingUrl ? '解析中...' : '確定'}</Text>
                    </TouchableOpacity>
                  </View>
                </>
              )}
            </View>
          </View>
        )}
      </View>
    );

  if (Platform.OS === 'web') {
    if (!visible) return null;
    // Use createPortal to render at document.body level to avoid being covered by header
    // z-index 9999 is lower than FullscreenWrapper (2147483640) so map can overlay on top
    return ReactDOM.createPortal(
      <SafeView style={styles.webOverlay}>
        <SafeView style={styles.webContent}>{mainContent}</SafeView>
      </SafeView>,
      document.body
    );
  }
  return (
    <Modal visible={visible} animationType="slide" transparent={false}>
      {mainContent}
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#fff' },
  scroll: { flex: 1, backgroundColor: '#fff' },
  headerContainer: { width: '100%', height: 220, position: 'relative', overflow: 'hidden', borderTopLeftRadius: 14, borderTopRightRadius: 14 },
  headerMapWrapper: { width: '100%', height: '100%', borderBottomWidth: 1, borderBottomColor: '#E5E7EB' },
  headerMapOverlay: { position: 'absolute', left: 0, right: 0, top: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.06)' },
  headerImageWrapper: { width: '100%', height: '100%', position: 'relative', overflow: 'hidden' },
  headerImage: { width: '100%', height: '100%', resizeMode: 'cover' as any },
  headerOverlay: { position: 'absolute', left: 0, right: 0, top: 0, height: '100%', backgroundColor: 'rgba(0,0,0,0.12)' },
  headerControlsOverlay: { position: 'absolute', left: 0, right: 0, top: 0, bottom: 0, zIndex: 10 },
  headerTopRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', paddingHorizontal: 12, paddingTop: 16 },
  headerIconButton: { width: 40, height: 40, borderRadius: 20, backgroundColor: 'rgba(0,0,0,0.4)', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(8px)' as any },
  headerRightButtons: { flexDirection: 'row', gap: 8 },
  attributionBadge: { position: 'absolute', bottom: 8, right: 8, backgroundColor: 'rgba(0,0,0,0.6)', paddingHorizontal: 8, paddingVertical: 4, borderRadius: 4, maxWidth: 200 },
  attributionText: { color: '#fff', fontSize: 11 },
  backButton: { position: 'absolute', left: 12, top: 16, width: 40, height: 40, borderRadius: 20, alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255,255,255,0.12)' },
  content: { paddingHorizontal: 24, paddingTop: 14, backgroundColor: '#fff' },
  title: { fontSize: 26, fontWeight: '800', color: '#111827', marginVertical: 8 },
  addressRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 12 },
  addressText: { marginLeft: 8, color: '#8E8E93', fontSize: 15 },
  section: { marginTop: 8, marginBottom: 8 },
  sectionHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: 8 },
  sectionHeaderText: { marginLeft: 8, fontSize: 18, fontWeight: '800', color: '#333' },
  // align description text with the section header text (icon width ~20 + marginLeft 8)
  description: { color: '#8E8E93', lineHeight: 24, paddingLeft: 28 },
  // tabs
  tabsRow: { flexDirection: 'row', alignItems: 'stretch', gap: 8 },
  tabBtn: { paddingVertical: 8, paddingHorizontal: 10, borderRadius: 8 },
  tabBtnActive: { backgroundColor: '#F3F4F6' },
  tabText: { fontSize: 14, color: '#6B7280', fontWeight: '600' },
  tabTextActive: { color: '#111827' },
  tabDivider: { height: 1, backgroundColor: '#E5E7EB', marginTop: 8, marginBottom: 12 },
  sectionDivider: { height: 1, backgroundColor: '#E5E7EB', marginVertical: 12 },
  // indent meta content so it lines up with the section header text (icon width + gap)
  metaBlock: { paddingVertical: 6, paddingLeft: 28 },
  metaRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 10 },
  metaRowDivider: { borderBottomWidth: 1, borderBottomColor: '#F3F4F6' },
  metaLabel: { color: '#8E8E93', fontSize: 15 },
  metaValue: { fontSize: 15, color: '#111', fontWeight: '700' },
  // tags should align with header text as well
  tagsRow: { flexDirection: 'row', flexWrap: 'wrap', marginTop: 8, paddingLeft: 28 },
  tagPill: { backgroundColor: '#E6F0FF', paddingHorizontal: 14, paddingVertical: 6, borderRadius: 999, marginRight: 8, marginBottom: 8 },
  tagText: { color: '#0B61D6', fontWeight: '700' },
  actionBtn: { minWidth: 40, height: 36, borderRadius: 8, alignItems: 'center', justifyContent: 'center', marginLeft: 8, paddingHorizontal: 10 },
  // debug block content should also align with other paragraph content
  debugBlock: { backgroundColor: '#FAFAFB', padding: 12, paddingLeft: 28, borderRadius: 8 },
  debugRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 6, borderBottomWidth: 1, borderBottomColor: '#F3F4F6' },
  debugKey: { color: '#6B7280', fontSize: 13, flex: 1 },
  debugVal: { color: '#111827', fontSize: 13, flex: 2, textAlign: 'right' },
  galleryThumb: { width: 104, height: 72, borderRadius: 8, marginRight: 10, backgroundColor: '#F3F4F6' },
  centerContainer: { alignItems: 'center', justifyContent: 'center', paddingTop: 80 },
  loadingText: { marginTop: 12, color: '#6B7280', fontSize: 15 },
  // Web overlay styles to ensure this modal always sits above map fullscreen overlay
  webOverlay: {
    position: 'fixed',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.35)',
    zIndex: 10100 as any, // higher than RouteMap (10000) and hero
    alignItems: 'center',
    justifyContent: 'flex-start',
    paddingTop: 20,
  },
  webContent: {
    width: '100%',
    maxWidth: 600,
    backgroundColor: '#fff',
    borderRadius: 14,
    overflow: 'hidden',
    maxHeight: '100%',
    flex: 1,
  },
  // Embed modal styles
  embedModalOverlay: {
    position: 'absolute' as any,
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 999999,
  },
  embedModalContent: {
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 24,
    width: '90%',
    maxWidth: 500,
    ...Platform.select({
      web: {
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      },
      default: {
        elevation: 24,
        shadowColor: '#000',
        shadowOpacity: 0.3,
        shadowRadius: 20,
      },
    }),
  },
  embedModalTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#111827',
    marginBottom: 16,
    textAlign: 'center',
  },
  embedModalInput: {
    borderWidth: 2,
    borderColor: '#FB7A4A',
    borderRadius: 12,
    padding: 14,
    fontSize: 16,
    color: '#111827',
    marginBottom: 20,
    minHeight: 120,
    maxHeight: 200,
  },
  embedModalButtons: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 12,
  },
  embedModalButton: {
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
    minWidth: 80,
    alignItems: 'center',
  },
  embedModalCancelButton: {
    backgroundColor: '#F3F4F6',
  },
  embedModalConfirmButton: {
    backgroundColor: '#FB7A4A',
  },
  embedModalCancelText: {
    color: '#6B7280',
    fontSize: 16,
    fontWeight: '600',
  },
  embedModalConfirmText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
});

export default FullscreenPoiModal;